import React from 'react';

import {
  TClientDetailsClientInfo,
  TClientDetailsStackInfo,
  TClientDetailsPaymentsInfo,
  Partner,
} from 'types/state';
import { useServices } from 'hooks/useServices';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServiceState } from 'hooks/useServiceState';
import { DONE_TIMEOUT_DURATION } from 'utils/constants';
import { setCustomTimeout } from 'utils/setCustomTimeout';
import { formatDate } from 'utils/formatDate';
import { normalizeURL } from 'utils/noramlizeURL';
import { useCountriesValues } from 'hooks/useCountriesValues';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { MarginComponent } from 'design/margin';
import {
  SmallTableRowComponent,
  SmallTableWrapperComponent,
} from 'design/smallTable';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { LinkControlComponent } from 'design/linkControl';

type Props = {
  clientInfo: TClientDetailsClientInfo;
  paymentsInfo: TClientDetailsPaymentsInfo;
  stackInfo: TClientDetailsStackInfo;
  partner: null | Partner;
  onEditProfile(): void;
  onEditLogin(): void;
  onEditPassword(): void;
  onEditPartnerBinding(): void;
  onPartnerBindingDeleted(): void;
  onArchiveActionDidSucceed(): void;
  onCreateRefund(): void;
};

export const ClientDetailsSummary = (props: Props) => {
  const {
    clientInfo,
    stackInfo,
    paymentsInfo,
    partner,
    onEditProfile,
    onEditLogin,
    onEditPartnerBinding,
    onPartnerBindingDeleted,
    onArchiveActionDidSucceed,
    onCreateRefund,
  } = props;

  const { prodStackInfo, demoStackInfo, managersCount } = stackInfo;

  const {
    missingTableDataFallbackText,
    clientDetails: intl,
    shared: sharedDictionary,
  } = useIntlDictionary();

  const formatDateValue = React.useCallback(
    (ISOdate: string) => {
      return sharedDictionary.dates.calendarDateWithTime(new Date(ISOdate));
    },
    [sharedDictionary.dates]
  );

  const {
    moveClientToArchive: moveClientToArchiveService,
    removeClientFromArchive: removeClientFromArchiveService,
    deleteClientPartnerBinding,
  } = useServices();

  const [
    moveClientToArchivePromiseState,
    moveClientToArchive,
  ] = useServiceState(moveClientToArchiveService);
  const [
    removeClientFromArchivePromiseState,
    removeClientFromArchive,
  ] = useServiceState(removeClientFromArchiveService);

  const archiveActionDidSucceed =
    (!!moveClientToArchivePromiseState &&
      moveClientToArchivePromiseState.resolved) ||
    (!!removeClientFromArchivePromiseState &&
      removeClientFromArchivePromiseState.resolved);

  const archiveActionIsPending =
    (!!moveClientToArchivePromiseState &&
      moveClientToArchivePromiseState.pending) ||
    (!!removeClientFromArchivePromiseState &&
      removeClientFromArchivePromiseState.pending);

  React.useEffect(() => {
    if (archiveActionDidSucceed) {
      return setCustomTimeout(() => {
        onArchiveActionDidSucceed();
      }, DONE_TIMEOUT_DURATION);
    }
  }, [archiveActionDidSucceed, onArchiveActionDidSucceed]);

  const deletePartnerBinding = React.useCallback(() => {
    deleteClientPartnerBinding({ clientId: clientInfo.id }).then(
      onPartnerBindingDeleted
    );
  }, [clientInfo.id, deleteClientPartnerBinding, onPartnerBindingDeleted]);

  const handleArchiveButtonClick = React.useCallback(() => {
    if (clientInfo.archived) {
      removeClientFromArchive({
        clientId: clientInfo.id,
      });
    } else {
      moveClientToArchive({
        clientId: clientInfo.id,
      });
    }
  }, [
    clientInfo.archived,
    clientInfo.id,
    moveClientToArchive,
    removeClientFromArchive,
  ]);

  const archiveButtonText = (() => {
    if (clientInfo.archived) {
      return intl.buttons.removeFromArchive;
    }

    return intl.buttons.moveToArchive;
  })();

  const hasCreateRefundButton = React.useMemo<boolean>(() => {
    if (clientInfo.archived) {
      return false;
    }

    return !!prodStackInfo;
  }, [clientInfo.archived, prodStackInfo]);

  const registeredOnText = clientInfo.registeredOn
    ? formatDateValue(clientInfo.registeredOn)
    : '-';

  const updatedOnText = clientInfo.updatedOn
    ? formatDateValue(clientInfo.updatedOn)
    : '-';

  const managersCountValue = managersCount;

  const prodStackURLValue = React.useMemo(() => {
    if (prodStackInfo && prodStackInfo.originURL) {
      return (
        <LinkControlComponent to={prodStackInfo.originURL} newTab>
          <TextComponent color="accent">
            {normalizeURL(prodStackInfo.originURL).toHostName()}
          </TextComponent>
        </LinkControlComponent>
      );
    }

    return null;
  }, [prodStackInfo]);

  const prodStackSubdomainValue = React.useMemo(() => {
    if (prodStackInfo && prodStackInfo.subdomain) {
      return <TextComponent>{prodStackInfo.subdomain}</TextComponent>;
    }

    return (
      <TextComponent color="secondary">
        {missingTableDataFallbackText}
      </TextComponent>
    );
  }, [missingTableDataFallbackText, prodStackInfo]);

  const { allCountries } = useCountriesValues();

  return (
    <>
      <SidebarModalSectionComponent
        content={
          <>
            <TextComponent size="mp" weight="medium">
              {intl.table.clientInfoTitle}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.fullNameLabel}</TextComponent>
                {clientInfo.fullName ? (
                  <TextComponent>{clientInfo.fullName}</TextComponent>
                ) : (
                  <TextComponent color="secondary">
                    {missingTableDataFallbackText}
                  </TextComponent>
                )}
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.positionLabel}</TextComponent>
                {clientInfo.position ? (
                  <TextComponent>{clientInfo.position}</TextComponent>
                ) : (
                  <TextComponent color="secondary">
                    {missingTableDataFallbackText}
                  </TextComponent>
                )}
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.locationLabel}</TextComponent>
                {clientInfo.location ? (
                  <TextComponent>
                    {new Map(allCountries).get(clientInfo.location)}
                  </TextComponent>
                ) : (
                  <TextComponent color="secondary">
                    {missingTableDataFallbackText}
                  </TextComponent>
                )}
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.residentLabel}</TextComponent>
                <TextComponent>
                  {intl.table.residentValue(clientInfo.resident)}
                </TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.emailLabel}</TextComponent>
                {clientInfo.email ? (
                  <TextComponent>{clientInfo.email}</TextComponent>
                ) : (
                  <TextComponent color="secondary">
                    {missingTableDataFallbackText}
                  </TextComponent>
                )}
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.phoneLabel}</TextComponent>
                {clientInfo.phone ? (
                  <TextComponent>{clientInfo.phone}</TextComponent>
                ) : (
                  <TextComponent color="secondary">
                    {missingTableDataFallbackText}
                  </TextComponent>
                )}
              </SmallTableRowComponent>
              {demoStackInfo && demoStackInfo.originURL && (
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.demoLabel}</TextComponent>
                  <TextComponent>
                    <LinkControlComponent to={demoStackInfo.originURL} newTab>
                      <TextComponent color="accent">
                        {normalizeURL(demoStackInfo.originURL).toHostName()}
                      </TextComponent>
                    </LinkControlComponent>
                  </TextComponent>
                </SmallTableRowComponent>
              )}
              <SmallTableRowComponent>
                <TextComponent>{intl.table.registeredOnLabel}</TextComponent>
                <TextComponent>{registeredOnText}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.updatedOnLabel}</TextComponent>
                <TextComponent>{updatedOnText}</TextComponent>
              </SmallTableRowComponent>
              <SmallTableRowComponent>
                <TextComponent>{intl.table.idLabel}</TextComponent>
                <TextComponent>{clientInfo.id}</TextComponent>
              </SmallTableRowComponent>
            </SmallTableWrapperComponent>
          </>
        }
        actions={
          <>
            {!clientInfo.archived && (
              <ModalPressControlComponent
                type="button"
                theme="secondary"
                onPress={onEditProfile}
              >
                {intl.buttons.editProfile}
              </ModalPressControlComponent>
            )}
            {!clientInfo.archived && (
              <>
                <MarginComponent top={20} />
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  onPress={onEditLogin}
                >
                  {intl.buttons.editLogin}
                </ModalPressControlComponent>
              </>
            )}
            <MarginComponent top={20} />
            <ModalPressControlComponent
              type="button"
              theme="secondary"
              onPress={handleArchiveButtonClick}
              disabled={archiveActionIsPending}
            >
              {archiveButtonText}
            </ModalPressControlComponent>
          </>
        }
        navigation={null}
      />
      {partner && (
        <SidebarModalSectionComponent
          content={
            <>
              <TextComponent size="mp" weight="medium">
                {intl.table.partnerBindingTitle}
              </TextComponent>
              <MarginComponent top={20} />
              <SmallTableWrapperComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.partnerCodeLabel}</TextComponent>
                  <TextComponent>{partner.code}</TextComponent>
                </SmallTableRowComponent>
                <SmallTableRowComponent>
                  <TextComponent>
                    {intl.table.partnerFullNameLabel}
                  </TextComponent>
                  <TextComponent>
                    {intl.table.partnerFullNameValue(partner.profile)}
                  </TextComponent>
                </SmallTableRowComponent>
              </SmallTableWrapperComponent>
            </>
          }
          actions={
            !clientInfo.archived && (
              <>
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  onPress={onEditPartnerBinding}
                >
                  {intl.buttons.editPartnerBinding}
                </ModalPressControlComponent>
                <MarginComponent top={20} />
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  onPress={deletePartnerBinding}
                >
                  {intl.buttons.deletePartnerBinding}
                </ModalPressControlComponent>
              </>
            )
          }
        />
      )}
      {partner === null && (
        <SidebarModalSectionComponent
          content={
            <>
              <TextComponent size="mp" weight="medium">
                {intl.table.partnerBindingTitle}
              </TextComponent>
              <MarginComponent top={20} />
              <SmallTableWrapperComponent>
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.partnerCodeLabel}</TextComponent>
                  <TextComponent color="secondary">
                    {missingTableDataFallbackText}
                  </TextComponent>
                </SmallTableRowComponent>
              </SmallTableWrapperComponent>
            </>
          }
          actions={
            !clientInfo.archived && (
              <>
                <ModalPressControlComponent
                  type="button"
                  theme="secondary"
                  onPress={onEditPartnerBinding}
                >
                  {intl.buttons.editPartnerBinding}
                </ModalPressControlComponent>
              </>
            )
          }
        />
      )}
      <SidebarModalSectionComponent
        content={
          <>
            <TextComponent size="mp" weight="medium">
              {intl.table.stackInfoTitle}
            </TextComponent>
            <MarginComponent top={20} />
            <SmallTableWrapperComponent>
              {!prodStackURLValue && prodStackSubdomainValue && (
                <SmallTableRowComponent>
                  <TextComponent>
                    {intl.table.stackSubdomainLabel}
                  </TextComponent>
                  <TextComponent>{prodStackSubdomainValue}</TextComponent>
                </SmallTableRowComponent>
              )}
              {prodStackURLValue && (
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.stackURLLabel}</TextComponent>
                  <TextComponent>{prodStackURLValue}</TextComponent>
                </SmallTableRowComponent>
              )}
              {prodStackInfo?.trialDue && (
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.trialDueLabel}</TextComponent>
                  <TextComponent>
                    {formatDate(prodStackInfo.trialDue, 'DD.MM.YY')}
                  </TextComponent>
                </SmallTableRowComponent>
              )}
              {prodStackInfo?.licenseDue && (
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.licenseDueLabel}</TextComponent>
                  <TextComponent>
                    {formatDate(prodStackInfo.licenseDue, 'DD.MM.YY')}
                  </TextComponent>
                </SmallTableRowComponent>
              )}
              <SmallTableRowComponent>
                <TextComponent>{intl.table.balanceLabel}</TextComponent>
                <TextComponent>
                  {sharedDictionary.moneyAmountText(
                    paymentsInfo.balance,
                    'USD'
                  )}
                </TextComponent>
              </SmallTableRowComponent>
              {managersCountValue !== null && managersCountValue === -1 && (
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.managersCountLabel}</TextComponent>
                  <TextComponent>
                    {intl.table.managersCountFailedValue}
                  </TextComponent>
                </SmallTableRowComponent>
              )}
              {managersCountValue !== null && managersCountValue > 0 && (
                <SmallTableRowComponent>
                  <TextComponent>{intl.table.managersCountLabel}</TextComponent>
                  <TextComponent>{managersCountValue}</TextComponent>
                </SmallTableRowComponent>
              )}
            </SmallTableWrapperComponent>
          </>
        }
        actions={
          !clientInfo.archived && (
            <>
              {hasCreateRefundButton && (
                <>
                  <MarginComponent top={20} />
                  <ModalPressControlComponent
                    type="button"
                    theme="secondary"
                    onPress={onCreateRefund}
                  >
                    {intl.buttons.createRefund}
                  </ModalPressControlComponent>
                </>
              )}
            </>
          )
        }
      />
    </>
  );
};
